import type { IScope } from "angular";
import "angular-translate";
import { finalize } from "rxjs/operators";
import { cloneDeep, isEmpty } from "lodash";
import "@progress/kendo-ui/js/kendo.autocomplete";

import type { IAccountRepository } from "App/Modules/Auth";
import type { IEnumService, ILabel, IResponseWithLoading, IRole, ITeam, ITeamMember } from "App/Modules/Models";
import { RoleFunctions } from "App/Modules/Models";
import type { IPartnerUsersRepository, IRolesRepository, ITeamsRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services";

export default class EditTeamMemberComponentController {
    public static $inject: string[] = [
        "$scope",
        "$translate",
        "irRolesRepository",
        "mmTeamsRepository",
        "mmPartnerUsersRepository",
        "mmEnumService",
        "mmAccountRepository",
    ];

    public partnerId: string;

    public team: ITeam;

    public teamMember: ITeamMember;

    public originalTeamMember: ITeamMember;

    public errorMessage: string;

    public editResponse: IResponseWithLoading<ITeamMember>;

    public deleteResponse: IResponseWithLoading<void>;

    public siteRoles: IResponseWithLoading<IRole[]>;

    public labelsResponse: IResponseWithLoading<ILabel[]>;

    public labels: string[];

    public partnerUserLabelsString: string;

    public originalPartnerUserLabelsString: string;

    public resolve: IFormContainerResolve<ITeamMember, ITeam>;

    constructor(
        private readonly $scope: IScope,
        private readonly $translate: angular.translate.ITranslateService,
        private readonly rolesRepository: IRolesRepository,
        private readonly teamsRepository: ITeamsRepository,
        private readonly partnerUsersRepository: IPartnerUsersRepository,
        private readonly enumService: IEnumService,
        private readonly accountRepository: IAccountRepository,
    ) {}

    public $onInit() {
        this.originalTeamMember = this.resolve.formData;
        this.team = this.resolve.bindings;
        this.partnerId = this.resolve.bindings.partnerId;
        this.teamMember = cloneDeep(this.originalTeamMember);

        this.partnerUserLabelsString =
            this.teamMember.labels.map((l) => l.label).join(",") + (this.teamMember.labels.length > 0 ? "," : "");
        this.originalPartnerUserLabelsString = cloneDeep(this.partnerUserLabelsString);

        extendWithLoadingAndFail(this.rolesRepository.getSiteRoles())
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    if (!response.hasError && response.data) {
                        const roles = [this.rolesRepository.getNoRole()].concat(response.data);
                        response.data = roles;
                    }
                    this.siteRoles = response;
                },
                (response) => {
                    this.siteRoles = response;
                },
            );

        extendWithLoadingAndFail(this.partnerUsersRepository.getLabels(this.partnerId))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    if (!response.hasError && response.data) {
                        this.labels = response.data.map((l) => l.label);
                    }
                    this.labelsResponse = response;
                },
                (error) => {
                    this.labelsResponse = error;
                },
            );
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public sortById(role: IRole) {
        return RoleFunctions.sortById(role);
    }

    public editTeamMember() {
        const patchObject = {} as ITeamMember;

        if (this.originalPartnerUserLabelsString !== this.partnerUserLabelsString) {
            patchObject.labels = this.partnerUserLabelsString
                .split(",")
                .map((l) => l.trim())
                .filter((l) => l !== "")
                .map((l) => ({ label: l } as ILabel));
        }

        if (
            (this.originalTeamMember.role === null && this.teamMember.role.id !== null) ||
            (this.originalTeamMember.role !== null && this.originalTeamMember.role.id !== this.teamMember.role.id)
        ) {
            patchObject.role =
                this.teamMember.role && this.teamMember.role.id ? ({ id: this.teamMember.role.id } as IRole) : null;
        }

        if (!isEmpty(patchObject)) {
            extendWithLoadingAndFail(
                this.teamsRepository.patchTeamMember(this.team.id, this.teamMember.partnerUser.id, patchObject),
            ).subscribe(
                (response: IResponseWithLoading<ITeamMember>) => {
                    this.editResponse = response;
                    if (!response.isLoading && response.data) {
                        this.resolve.options.onOk(response.data);
                    }
                },
                (errorResponse: IResponseWithLoading<ITeamMember>) => {
                    this.editResponse = errorResponse;
                },
            );
        } else {
            this.cancel();
        }
    }

    public deleteTeamMember() {
        extendWithLoadingAndFail(
            this.teamsRepository.deleteTeamMember(this.team.id, this.teamMember.partnerUser.id),
        ).subscribe((response: IResponseWithLoading<void>) => {
            this.deleteResponse = response;
            if (!response.isLoading && !response.hasError) {
                this.resolve.options.onOk(null);
            }
        });
    }
}
